import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { pickLocalized } from '@mrigl/shared';
import { Alert, Button, Card } from '@mrigl/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import { useI18n } from '../i18n.js';
import { useSession } from '../session.js';

type Preview = Schemas['InvitationPreview'];

/** Invitation acceptance (reached from the email link, after sign-in). */
export function InvitationPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void unwrap(api.GET('/admin-api/v1/invitations/{token}', { params: { path: { token } } }))
      .then(setPreview)
      .catch((failure: unknown) => {
        if (
          failure instanceof ApiError &&
          (failure.code === 'not-found' || failure.code === 'validation-failed')
        ) {
          setInvalid(true);
        } else {
          setError(errorMessage(t, failure));
        }
      });
  }, [token, t]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const result = await unwrap(
        api.POST('/admin-api/v1/invitations/{token}/accept', { params: { path: { token } } }),
      );
      await refresh();
      void navigate(`/stores/${result.storeId}`, { replace: true });
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (invalid) return <Alert tone="error">{t('admin.invitation.invalid')}</Alert>;
  if (error !== null && preview === null) return <Alert tone="error">{error}</Alert>;
  if (preview === null) return <Loading />;

  const store = pickLocalized(preview.storeName, locale, 'fr') ?? '';
  return (
    <Card labelledBy="invitation-title">
      <h1 id="invitation-title" className="mrigl-heading">
        {t('admin.invitation.title', { store })}
      </h1>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {preview.emailMatches ? (
        <>
          <p>
            {t('admin.invitation.intro', {
              store,
              role: t(`admin.role.${preview.role}` as MessageKey),
            })}
          </p>
          <Button busy={busy} onClick={() => void accept()}>
            {t('admin.invitation.accept')}
          </Button>
        </>
      ) : (
        <Alert tone="error">
          {t('admin.invitation.mismatch', { email: '⁦' + preview.emailHint + '⁩' })}
        </Alert>
      )}
    </Card>
  );
}
